import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ANNO_COLORS,
  ANNO_TEXT_PT_SIZES,
  DEFAULT_ANNO_COLOR,
  DEFAULT_ANNO_TEXT_SIZE,
  DEFAULT_ARROW_HEAD_SIZE,
  INSPECTION_LAYOUTS,
  INSPECTION_LAYOUT_IDS,
  MAX_ARROW_HEAD_SIZE,
  MIN_ARROW_HEAD_SIZE,
  annoPtToUnits,
  annoUnitsToPt,
  getLayout,
  slotClass,
  type AnnoColor,
  type Annotation,
  type InspectionLayoutId,
  type InspectionReportDocument,
  type JobFileEntry,
} from '@jobos/shared';

import { Button } from '@/components/ui/button';
import { InspectionReportStatusBadge } from '@/components/shared/InspectionReportStatusBadge';
import { type Editor } from '@tiptap/react';
import { MousePointer2, ArrowLeft, ArrowUp, ArrowDown, ArrowUpRight, Minus, MoveHorizontal, Circle as CircleIcon, Square as SquareIcon, Copy, Scissors, ClipboardPaste, Type, Trash2, Undo2, Redo2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { SaveStatusIndicator } from '@/components/save/SaveStatusIndicator';
import { SaveErrorBanner } from '@/components/save/SaveErrorBanner';
import { RecoverEditsDialog } from '@/components/breakdown-chrome/RecoverEditsDialog';
import { RichTextToolbar } from './RichTextToolbar';
import type { AnnotationTool } from './AnnotationLayer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAutosave } from '@/hooks/useAutosave';
import { useHistory } from '@/hooks/useHistory';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useJob } from '@/hooks/useJobs';
import { usePromoteFileToLocationPhoto } from '@/hooks/useLocations';
import {
  inspectionReportKeys,
  useInspectionReport,
  useUpdateInspectionReport,
  useGenerateInspectionPdf,
  type InspectionReport,
} from '@/hooks/useInspectionReports';
import {
  addPage,
  duplicatePage,
  removePage,
  reorderPages,
  setBlock,
  setBlockVAlign,
  setHeader,
  setSlotCaption,
  setSlotPhoto,
  clearSlotPhoto,
  setSlotTransform,
  setPageAnnotations,
  slotPhotoClip,
  pasteSlotPhoto,
  clampPanOffset,
  type SlotPhotoClip,
} from '@/lib/inspectionDocument';
import { liftAnnotationToPage } from '@/lib/inspectionLift';
import { fetchJobFileUrl, useUploadJobPhoto } from '@/hooks/useJobFiles';
import { ANNOTATION_PASTE_OFFSET, cloneAnnotation, segmentSize, setAnnotationColor, setSegmentSize, setTextSize } from '@/lib/inspectionAnnotations';
import { PageCanvas } from './PageCanvas';
import { LayoutThumbnail } from './LayoutThumbnail';
import { CoverSheet } from './CoverSheet';
import { JobPhotoPickerDialog } from '@/pages/jobs/components/files/JobPhotoPickerDialog';
import { fillSlotsWithPhotos, planPhotoFill } from './photoFill';
import { planSlotDrop } from './slotDrop';
import { PhotoNavigator } from './PhotoNavigator';
import { useSlotAspect } from './useSlotAspect';
import { useToast } from '@/hooks/use-toast';
import './inspection-editor.css';

// Sentinel activePageId for the generated cover sheet (not a real page).
const COVER = 'cover';

// Modifier the tooltips name, so they quote the key the user actually has under
// their hand rather than a generic "Cmd/Ctrl".
const MOD_KEY = /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘' : 'Ctrl+';

type Region = { kind: 'slot' | 'block'; key: string } | null;

// Where a pick from the job photo library is headed: a page slot, or the cover
// sheet's building photo (which holds one photo, on the header rather than a page).
type PickerTarget = { kind: 'slot'; slotKey: string } | { kind: 'cover' } | null;

export default function InspectionReportEditor() {
  const { jobId, reportId } = useParams<{ jobId: string; reportId: string }>();
  const { data, isLoading } = useInspectionReport(reportId);
  const { data: job } = useJob(jobId);
  const promotePhotoToAddress = usePromoteFileToLocationPhoto();
  const isOnline = useOnlineStatus();
  const updateMutation = useUpdateInspectionReport(reportId ?? '');
  // Photos shot for a report go into the job's library like any other job photo,
  // and the slot holds the row's display-rendition key. Nothing here writes to
  // the legacy inspection-photos/ prefix any more.
  const uploadPhoto = useUploadJobPhoto(jobId ?? '');
  const generatePdf = useGenerateInspectionPdf(reportId ?? '');

  // Straight off the server copy rather than local state: the status is not
  // part of the document the editor edits, and autosave never writes it.
  const status = data?.report.status ?? 'draft';
  const sentAt = data?.report.sentAt ?? null;

  const handleToggleSent = () => {
    updateMutation.mutate({ status: status === 'sent' ? 'draft' : 'sent' });
  };

  const [doc, setDoc] = useState<InspectionReportDocument | null>(null);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [activePageId, setActivePageId] = useState<string | null>(null);
  const [region, setRegion] = useState<Region>(null);
  const [activeEditor, setActiveEditor] = useState<Editor | null>(null);
  const [hoverLayout, setHoverLayout] = useState<InspectionLayoutId>(INSPECTION_LAYOUT_IDS[0]);
  const [annotationTool, setAnnotationTool] = useState<AnnotationTool>('select');
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null);
  // Sizes (viewBox units) the next drawn note/arrow gets — they follow the last
  // size the user picked, so drawing a run of same-sized annotations is one pick.
  const [newTextSize, setNewTextSize] = useState(DEFAULT_ANNO_TEXT_SIZE);
  const [newArrowHeadSize, setNewArrowHeadSize] = useState(DEFAULT_ARROW_HEAD_SIZE);
  // Same idea for color: the last swatch picked is what the next annotation is
  // drawn in, whatever its type.
  const [newColor, setNewColor] = useState<AnnoColor>(DEFAULT_ANNO_COLOR);
  // Annotation clipboard — internal to the editor, not the OS one, so copying an
  // annotation never disturbs whatever text the user has on the system clipboard.
  // `lastPageId` is the page the copy came from (or was last pasted onto), which
  // is what decides whether the next paste offsets or lands in place.
  const [annoClipboard, setAnnoClipboard] = useState<{ annotation: Annotation; lastPageId: string } | null>(null);
  // Photo clipboard — the slot equivalent, and equally internal to the editor.
  // Holds the photo and its framing only; a slot's caption stays with the slot
  // (see SlotPhotoClip). Empty until something is copied, and never populated
  // from the OS clipboard.
  const [photoClipboard, setPhotoClipboard] = useState<SlotPhotoClip | null>(null);
  const [pageToDelete, setPageToDelete] = useState<string | null>(null);
  const [uploadingSlotKey, setUploadingSlotKey] = useState<string | null>(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [pendingDrop, setPendingDrop] = useState<{ slotKey: string; file: File } | null>(null);
  const [picker, setPicker] = useState<PickerTarget>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // DOM nodes for each page's thumbnail subtree, so auto-lift measures THAT page's
  // own slots (slot keys repeat across pages — never query the whole document).
  const pageNodeRefs = useRef<Map<string, HTMLElement>>(new Map());
  const liftedRef = useRef(false);

  // Reset annotation selection/tool when the active page changes. Annotation
  // editing is page-scoped now, so selecting a slot/block must not clear it.
  useEffect(() => {
    setSelectedAnnotationId(null);
    setAnnotationTool('select');
  }, [activePageId]);

  // Seed local state once the report loads.
  useEffect(() => {
    if (data?.report && doc === null) {
      setDoc(data.report.document);
      setPhotoUrls(data.photoUrls);
      setActivePageId(COVER); // open on the cover sheet
    }
  }, [data, doc]);

  /**
   * Record a photo's URL for this session and for the next one.
   *
   * Local state draws the page now; the cached detail is what the editor seeds
   * from when it is re-opened inside the query's freshness window, and a key it
   * has no URL for renders as an empty slot.
   */
  const rememberPhotoUrls = useCallback(
    (urls: Record<string, string>) => {
      setPhotoUrls((prev) => ({ ...prev, ...urls }));
      queryClient.setQueryData<{ report: InspectionReport; photoUrls: Record<string, string> }>(
        inspectionReportKeys.detail(reportId!),
        (prev) => (prev ? { ...prev, photoUrls: { ...prev.photoUrls, ...urls } } : prev),
      );
    },
    [queryClient, reportId],
  );

  /**
   * Presign the display rendition of each library row and remember the URLs.
   *
   * Best effort, whichever way the photo arrived: a presign that fails leaves
   * the file id in the document, and the photo resolves on the next report load.
   */
  const rememberEntryUrls = useCallback(
    async (entries: JobFileEntry[]) => {
      const resolved = await Promise.allSettled(
        entries.map((entry) => fetchJobFileUrl(queryClient, jobId!, entry.id)),
      );
      const urls: Record<string, string> = {};
      resolved.forEach((result, i) => {
        // Keyed by file id, the same value the slot stores and the canvas
        // looks up.
        if (result.status === 'fulfilled') urls[entries[i]!.id] = result.value;
      });
      rememberPhotoUrls(urls);
    },
    [jobId, queryClient, rememberPhotoUrls],
  );

  const saveFn = useCallback(
    async (value: InspectionReportDocument) => {
      await updateMutation.mutateAsync({ document: value, title: value.header.title });
    },
    [updateMutation],
  );

  const autosave = useAutosave<InspectionReportDocument>({
    offlineKey: `inspection-report-${reportId}`,
    isOnline,
    saveFn,
    backupKind: 'inspection-report',
  });

  // Flush any pending save when leaving the editor. Keyed on the (stable)
  // callback rather than the autosave object, which is a new literal every
  // render — depending on it would re-run this cleanup per render and save on
  // every keystroke.
  const flushPendingSave = autosave.flushPendingSave;
  useEffect(() => {
    return () => {
      void flushPendingSave().catch(() => {});
    };
  }, [flushPendingSave]);

  const history = useHistory<InspectionReportDocument>({ clone: (d) => structuredClone(d) });

  // Apply a pure document op: record the pre-edit doc for undo (debounced so a
  // burst of edits collapses into one step), update local state, queue autosave.
  const apply = useCallback(
    (next: InspectionReportDocument) => {
      if (doc) history.markDirty(doc);
      setDoc(next);
      autosave.setPending(next);
    },
    [autosave, history, doc],
  );

  const restore = (snapshot: InspectionReportDocument | null) => {
    if (!snapshot) return;
    setDoc(snapshot);
    autosave.setPending(snapshot);
    setSelectedAnnotationId(null);
    if (!snapshot.pages.some((p) => p.id === activePageId)) {
      setActivePageId(snapshot.pages[0]?.id ?? null);
    }
  };
  const handleUndo = () => doc && restore(history.undo(doc));
  const handleRedo = () => doc && restore(history.redo(doc));

  const activePage = useMemo(
    () => doc?.pages.find((p) => p.id === activePageId) ?? null,
    [doc, activePageId],
  );

  // Every photo this report already places, cover included. The library picker
  // marks these rather than hiding them — the same photo cropped two ways is a
  // normal thing to want.
  const usedFileIds = useMemo(() => {
    const keys = new Set<string>();
    if (doc?.header.coverFileId) keys.add(doc.header.coverFileId);
    for (const page of doc?.pages ?? []) {
      for (const slot of Object.values(page.slots)) {
        if (slot.fileId) keys.add(slot.fileId);
      }
    }
    return keys;
  }, [doc]);

  // Live aspect ratio of the selected slot, so the photo navigator matches its shape.
  const slotAspect = useSlotAspect(
    activePageId === COVER ? null : activePageId,
    region?.kind === 'slot' ? region.key : null,
  );

  const selectedAnnotation = useMemo(
    () => (activePage?.annotations ?? []).find((a) => a.id === selectedAnnotationId) ?? null,
    [activePage, selectedAnnotationId],
  );

  const deleteSelectedAnnotation = useCallback(() => {
    if (!selectedAnnotationId || !doc || !activePage) return;
    apply(setPageAnnotations(doc, activePage.id, (activePage.annotations ?? []).filter((a) => a.id !== selectedAnnotationId)));
    setSelectedAnnotationId(null);
  }, [selectedAnnotationId, doc, activePage, apply]);

  // Copy / cut / paste of the selected annotation. Cut and paste both go through
  // `apply`, so they land in the undo history; copy changes nothing but the
  // clipboard.
  const copyAnnotation = useCallback(() => {
    if (!selectedAnnotation || !activePage) return;
    setAnnoClipboard({ annotation: selectedAnnotation, lastPageId: activePage.id });
  }, [selectedAnnotation, activePage]);

  const cutAnnotation = useCallback(() => {
    if (!selectedAnnotation) return;
    copyAnnotation();
    deleteSelectedAnnotation();
  }, [selectedAnnotation, copyAnnotation, deleteSelectedAnnotation]);

  const pasteAnnotation = useCallback(() => {
    if (!annoClipboard || !doc || !activePage) return;
    // Back onto the page it came from: step the copy away from the last one, so
    // repeated pastes cascade instead of stacking invisibly on top of each other.
    // Onto a different page: land in the identical spot — reusing the same note
    // in the same place across pages is the reason to copy it at all.
    const d = annoClipboard.lastPageId === activePage.id ? ANNOTATION_PASTE_OFFSET : 0;
    const pasted = cloneAnnotation(annoClipboard.annotation, d, d);
    apply(setPageAnnotations(doc, activePage.id, [...(activePage.annotations ?? []), pasted]));
    setAnnoClipboard({ annotation: pasted, lastPageId: activePage.id });
    setSelectedAnnotationId(pasted.id);
    setAnnotationTool('select');
  }, [annoClipboard, doc, activePage, apply]);

  // The slot the photo clipboard acts on: a selected slot on a real page whose
  // layout actually defines that key. `region` survives a page switch and slot
  // keys repeat across layouts, so without the layout check a paste after
  // switching pages could write a slot the new layout has no cell for.
  const selectedSlotKey = useMemo(() => {
    if (!activePage || activePageId === COVER || region?.kind !== 'slot') return null;
    const exists = getLayout(activePage.layoutType).slots.some((s) => s.key === region.key);
    return exists ? region.key : null;
  }, [activePage, activePageId, region]);

  const selectedSlotClip = useMemo(
    () => (activePage && selectedSlotKey ? slotPhotoClip(activePage, selectedSlotKey) : null),
    [activePage, selectedSlotKey],
  );

  // Copy / cut / paste of the selected slot's photo, mirroring the annotation
  // trio above: cut and paste go through `apply` (undo + autosave), copy only
  // touches the clipboard.
  const copySlotPhoto = useCallback(() => {
    if (!selectedSlotClip) return;
    setPhotoClipboard(selectedSlotClip);
  }, [selectedSlotClip]);

  const cutSlotPhoto = useCallback(() => {
    if (!selectedSlotClip || !doc || !activePage || !selectedSlotKey) return;
    setPhotoClipboard(selectedSlotClip);
    apply(clearSlotPhoto(doc, activePage.id, selectedSlotKey));
  }, [selectedSlotClip, doc, activePage, selectedSlotKey, apply]);

  // Paste replaces whatever is in the slot; undo is the way back. The clipboard
  // keeps its contents, so one copied photo can fill several slots.
  const pastePhotoIntoSlot = useCallback(() => {
    if (!photoClipboard || !doc || !activePage || !selectedSlotKey) return;
    apply(pasteSlotPhoto(doc, activePage.id, selectedSlotKey, photoClipboard));
  }, [photoClipboard, doc, activePage, selectedSlotKey, apply]);

  // Keyboard on the selected page-level annotation: Backspace/Delete removes it,
  // Cmd/Ctrl+C/X/V copy, cut and paste it. All of them stand down while the user
  // is typing, so the rich-text blocks and caption fields keep native behaviour.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (!doc || !activePage || activePageId === COVER) return;

      if (e.key === 'Backspace' || e.key === 'Delete') {
        if (!selectedAnnotationId) return;
        e.preventDefault();
        deleteSelectedAnnotation();
        return;
      }

      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      // Only claim the shortcut when there is something to act on — a plain
      // Cmd+C with no annotation selected still copies whatever the browser
      // would have copied.
      //
      // Both clipboards can hold something at once, so selection decides which
      // one the shortcut means. A selected annotation always wins — clicking a
      // slot clears the annotation selection, so this reads as "whatever you
      // touched last". Otherwise a selected slot takes C/X, and takes V when
      // there is a photo to paste; V with an empty photo clipboard still falls
      // through to the annotation paste.
      const key = e.key.toLowerCase();
      if (key === 'c' && selectedAnnotation) {
        e.preventDefault();
        copyAnnotation();
      } else if (key === 'x' && selectedAnnotation) {
        e.preventDefault();
        cutAnnotation();
      } else if (key === 'c' && selectedSlotClip) {
        e.preventDefault();
        copySlotPhoto();
      } else if (key === 'x' && selectedSlotClip) {
        e.preventDefault();
        cutSlotPhoto();
      } else if (key === 'v' && !selectedAnnotation && selectedSlotKey && photoClipboard) {
        e.preventDefault();
        pastePhotoIntoSlot();
      } else if (key === 'v' && annoClipboard) {
        e.preventDefault();
        pasteAnnotation();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    selectedAnnotationId,
    selectedAnnotation,
    annoClipboard,
    doc,
    activePage,
    activePageId,
    deleteSelectedAnnotation,
    copyAnnotation,
    cutAnnotation,
    pasteAnnotation,
    selectedSlotKey,
    selectedSlotClip,
    photoClipboard,
    copySlotPhoto,
    cutSlotPhoto,
    pastePhotoIntoSlot,
  ]);

  // One-shot migration: lift any legacy per-slot annotations onto the page-level
  // layer using measured DOM geometry, so old reports open with editable overlays.
  // Written via setDoc + autosave.setPending (NOT apply) so it never enters undo.
  useEffect(() => {
    if (!doc || liftedRef.current) return;
    const needsLift = doc.pages.some((p) => Object.values(p.slots).some((s) => s.annotations.length > 0));
    if (!needsLift) { liftedRef.current = true; return; }
    const raf = requestAnimationFrame(() => {
      let changed = false;
      const pages = doc.pages.map((page) => {
        const node = pageNodeRefs.current.get(page.id);
        const pageEl = node?.querySelector('.ins-page') as HTMLElement | null;
        if (!pageEl) return page; // can't measure -> leave legacy (dual-render still correct)
        const pr = pageEl.getBoundingClientRect();
        const pageRect = { x: pr.x, y: pr.y, width: pr.width, height: pr.height };
        const lifted = [...(page.annotations ?? [])];
        const slots = { ...page.slots };
        let pageChanged = false;
        for (const [key, slot] of Object.entries(page.slots)) {
          if (!slot.annotations.length) continue;
          const slotEl = pageEl.querySelector('.' + slotClass(key)) as HTMLElement | null;
          if (!slotEl) continue; // leave this slot's annotations as legacy
          const sr = slotEl.getBoundingClientRect();
          const slotRect = { x: sr.x, y: sr.y, width: sr.width, height: sr.height };
          const fontPx = sr.height * 3.5 / 100; // rendered .ins-anno-text height in the same scaled space as pageRect
          for (const a of slot.annotations) {
            lifted.push(liftAnnotationToPage(pageRect, slotRect, a, a.type === 'text' ? fontPx : undefined));
          }
          slots[key] = { ...slot, annotations: [] };
          pageChanged = true;
        }
        if (!pageChanged) return page;
        changed = true;
        return { ...page, annotations: lifted, slots };
      });
      liftedRef.current = true;
      if (changed) { const next = { ...doc, pages }; setDoc(next); autosave.setPending(next); }
    });
    return () => cancelAnimationFrame(raf);
  }, [doc, autosave]);

  if (isLoading || !doc) {
    return <div className="ins-editor-loading">Loading report…</div>;
  }

  const jobStreet = [job?.location?.addressLine1, job?.location?.addressLine2].filter(Boolean).join(', ');
  const jobCity = job?.location?.city ?? '';
  const jobAddress = [jobStreet, jobCity].filter(Boolean).join(', ');
  const coverFileId = doc?.header.coverFileId ?? null;
  const coverPhotoUrl = coverFileId ? photoUrls[coverFileId] : undefined;

  const handleAddPage = (layoutType: InspectionLayoutId) => {
    const next = addPage(doc, layoutType);
    apply(next);
    setActivePageId(next.pages[next.pages.length - 1]!.id);
  };

  // The copy lands directly after its source and becomes the active page, so
  // the next edit applies to the duplicate rather than to what was copied.
  const handleDuplicatePage = (pageId: string) => {
    const index = doc.pages.findIndex((p) => p.id === pageId);
    if (index === -1) return;
    const next = duplicatePage(doc, pageId);
    apply(next);
    setActivePageId(next.pages[index + 1]!.id);
    setRegion(null);
    setSelectedAnnotationId(null);
  };

  // One user action, both outcomes: the photo joins the job's Files tab and
  // fills the slot. The slot stores the file id; which rendition gets read is
  // the resolver's business, not the document's.
  const handleUpload = async (slotKey: string, file: File) => {
    if (!activePage) return;
    setUploadingSlotKey(slotKey);
    try {
      const entry = await uploadPhoto.mutateAsync(file);
      await rememberEntryUrls([entry]);
      apply(setSlotPhoto(doc, activePage.id, slotKey, entry.id));
      setRegion({ kind: 'slot', key: slotKey });
    } catch {
      // The mutation already toasted; the slot stays as it was.
    } finally {
      setUploadingSlotKey(null);
    }
  };

  // Setting a cover also gives the address its photo — but only if it has none.
  // The backend refuses to overwrite an existing one, so this is safe to fire on
  // every cover change, and a failure here must never disturb the edit.
  const adoptCoverAsAddressPhoto = (fileId: string) => {
    if (!job?.locationId) return;
    promotePhotoToAddress.mutate(
      { locationId: job.locationId, fileId },
      {
        onSuccess: (result) => {
          if (result.promoted) {
            toast({ title: 'Also saved as the photo for this address' });
          }
        },
        onError: () => {
          // Silent: the cover is set, which is what the user asked for.
        },
      },
    );
  };

  const handleCoverUpload = async (file: File) => {
    if (!doc) return;
    setCoverUploading(true);
    try {
      const entry = await uploadPhoto.mutateAsync(file);
      await rememberEntryUrls([entry]);
      apply(setHeader(doc, { coverFileId: entry.id }));
      adoptCoverAsAddressPhoto(entry.id);
    } catch {
      // Toasted by the mutation.
    } finally {
      setCoverUploading(false);
    }
  };

  // Cover photo drop. Unlike a page slot, replacing here loses nothing (no zoom
  // or annotations), so a drop onto a filled cover uploads without confirming.
  const handleCoverDrop = (files: File[]) => {
    const plan = planSlotDrop(files, false);
    if (plan.kind === 'reject') {
      toast({ title: 'Cannot add photo', description: plan.message, variant: 'destructive' });
    } else if (plan.kind === 'upload') {
      void handleCoverUpload(plan.file);
    }
  };

  // Image file(s) dropped onto a slot: validate, then upload into an empty slot
  // or ask before replacing a filled one (replacing drops its zoom/annotations).
  const handleDropPhoto = (slotKey: string, files: File[]) => {
    if (!activePage) return;
    const slotFilled = !!activePage.slots[slotKey]?.fileId;
    const plan = planSlotDrop(files, slotFilled);
    if (plan.kind === 'reject') {
      toast({ title: 'Cannot add photo', description: plan.message, variant: 'destructive' });
    } else if (plan.kind === 'upload') {
      void handleUpload(slotKey, plan.file);
    } else if (plan.kind === 'confirm') {
      setPendingDrop({ slotKey, file: plan.file });
    }
  };

  // The slots a multi-pick would fill, and so how many photos the picker lets
  // the user choose. The cover holds exactly one.
  const fillPlan =
    picker?.kind === 'slot' && activePage
      ? planPhotoFill(
          getLayout(activePage.layoutType).slots.map((s) => s.key),
          activePage.slots,
          picker.slotKey,
        )
      : [];

  /** Places this opening of the picker has to fill: one cover, or the empty slots. */
  const pickerSlots = picker?.kind === 'cover' ? 1 : Math.max(1, fillPlan.length);

  // A photo chosen from the job library. The slot stores the file's display-
  // rendition key exactly as an uploaded photo would, so nothing downstream —
  // preview, zoom, annotations, PDF — can tell the two apart.
  const handlePickedPhotos = async (entries: JobFileEntry[]) => {
    if (!doc || entries.length === 0) return;
    const target = picker;
    if (!target) return;

    await rememberEntryUrls(entries);

    if (target.kind === 'cover') {
      apply(setHeader(doc, { coverFileId: entries[0]!.id }));
      adoptCoverAsAddressPhoto(entries[0]!.id);
      return;
    }
    if (!activePage) return;

    // One document, one apply: a four-photo fill is a single autosave and a
    // single undo, not four of each.
    apply(
      fillSlotsWithPhotos(
        doc,
        activePage.id,
        fillPlan,
        entries.map((entry) => entry.id),
      ),
    );
    setRegion({ kind: 'slot', key: fillPlan[0] ?? target.slotKey });
  };

  const handleGeneratePdf = async () => {
    await autosave.flushPendingSave();
    const url = await generatePdf.mutateAsync();
    // Presigned URL carries Content-Disposition: attachment, so this downloads.
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };


  return (
    <div className="ins-editor">
      <header className="ins-editor-top">
        {/* The editor is a full-screen route with no app chrome around it, so
            without this link the only way back to the job is the browser
            button. Autosave persists on the way out. */}
        <Link className="ins-editor-back" to={`/jobs/${jobId}?tab=inspections`}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Link>
        <span className="ins-editor-title-text">{doc.header.title || 'Untitled report'}</span>
        <div className="ins-editor-top-right">
          <button className="ins-icon-btn" title="Undo" disabled={!history.canUndo} onClick={handleUndo}>
            <Undo2 className="h-4 w-4" />
          </button>
          <button className="ins-icon-btn" title="Redo" disabled={!history.canRedo} onClick={handleRedo}>
            <Redo2 className="h-4 w-4" />
          </button>
          <SaveStatusIndicator
            isOnline={isOnline}
            isSaving={autosave.isSaving}
            isDirty={autosave.isDirty}
            hasOfflineEdits={autosave.hasOfflineEdits}
            hasContent={doc.pages.length > 0}
            onSave={() => void autosave.save()}
          />
          <InspectionReportStatusBadge status={status} sentAt={sentAt} />
          <Button
            variant="outline"
            onClick={handleToggleSent}
            disabled={updateMutation.isPending}
          >
            {status === 'sent' ? 'Mark as draft' : 'Mark as sent'}
          </Button>
          <Button onClick={handleGeneratePdf} disabled={generatePdf.isPending}>
            {generatePdf.isPending ? 'Generating…' : 'Download PDF'}
          </Button>
        </div>
      </header>

      {autosave.saveError && (
        <SaveErrorBanner
          error={autosave.saveError}
          isSaving={autosave.isSaving}
          onRetry={() => void autosave.save()}
          onDownloadBackup={() => autosave.downloadBackup(reportId ?? 'inspection-report')}
        />
      )}

      <div className="ins-editor-body">
        {/* Page thumbnail strip */}
        <aside className="ins-editor-pages">
          <div
            className={`ins-page-thumb${activePageId === COVER ? ' active' : ''}`}
            onClick={() => { setActivePageId(COVER); setRegion(null); }}
          >
            <div className="ins-page-thumb-head">
              <span className="ins-page-thumb-num">Cover</span>
            </div>
            <div className="ins-page-thumb-preview">
              <div className="ins-page-thumb-scale">
                <div className="ins-canvas-page-wrap">
                  {/* Read-only: no pick/drop handlers, so the photo renders as
                      it will on the page rather than as an editable slot. */}
                  <CoverSheet
                    title={doc.header.title}
                    address={jobStreet}
                    city={jobCity}
                    date={doc.header.date}
                    jobCode={job?.jobCode ?? ''}
                    issueSummary={doc.header.issueSummary}
                    {...(coverPhotoUrl ? { photoUrl: coverPhotoUrl } : {})}
                  />
                </div>
              </div>
            </div>
          </div>
          {doc.pages.map((p, i) => (
            <div
              key={p.id}
              className={`ins-page-thumb${p.id === activePageId ? ' active' : ''}`}
              onClick={() => setActivePageId(p.id)}
            >
              <div className="ins-page-thumb-head">
                <span className="ins-page-thumb-num">{i + 1}</span>
                <div className="ins-page-thumb-actions">
                  <button title="Move up" disabled={i === 0} onClick={(e) => { e.stopPropagation(); apply(reorderPages(doc, i, i - 1)); setActivePageId(p.id); setRegion(null); }}><ArrowUp /></button>
                  <button title="Move down" disabled={i === doc.pages.length - 1} onClick={(e) => { e.stopPropagation(); apply(reorderPages(doc, i, i + 1)); setActivePageId(p.id); setRegion(null); }}><ArrowDown /></button>
                  <button title="Duplicate" onClick={(e) => { e.stopPropagation(); handleDuplicatePage(p.id); }}><Copy /></button>
                  <button title="Delete" onClick={(e) => { e.stopPropagation(); setPageToDelete(p.id); }}><Trash2 /></button>
                </div>
              </div>
              <div className="ins-page-thumb-preview">
                <div
                  className="ins-page-thumb-scale"
                  ref={(el) => { if (el) pageNodeRefs.current.set(p.id, el); }}
                >
                  <PageCanvas page={p} photoUrls={photoUrls} />
                </div>
              </div>
            </div>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ins-add-page">+ Add page</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="ins-layout-menu">
              <div className="ins-layout-menu-list">
                {INSPECTION_LAYOUT_IDS.map((id) => (
                  <DropdownMenuItem
                    key={id}
                    onMouseEnter={() => setHoverLayout(id)}
                    onClick={() => handleAddPage(id)}
                  >
                    {INSPECTION_LAYOUTS[id].label}
                  </DropdownMenuItem>
                ))}
              </div>
              <div className="ins-layout-menu-preview">
                <LayoutThumbnail id={hoverLayout} />
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </aside>

        {/* Canvas */}
        <main className="ins-editor-canvas">
          {activePageId === COVER ? (
            <div className="ins-canvas-page-wrap">
              <CoverSheet
                title={doc.header.title}
                address={jobStreet}
                city={jobCity}
                date={doc.header.date}
                jobCode={job?.jobCode ?? ''}
                issueSummary={doc.header.issueSummary}
                {...(coverPhotoUrl ? { photoUrl: coverPhotoUrl } : {})}
                onPickPhoto={() => setPicker({ kind: 'cover' })}
                onDropPhoto={handleCoverDrop}
                isUploadingPhoto={coverUploading}
              />
            </div>
          ) : activePage ? (
            <PageCanvas
              page={activePage}
              photoUrls={photoUrls}
              selectedRegion={region}
              onSelectSlot={(key) => setRegion({ kind: 'slot', key })}
              onSelectBlock={(key) => setRegion({ kind: 'block', key })}
              onChangeBlock={(key, value) => apply(setBlock(doc, activePage.id, key, value))}
              onActiveEditor={setActiveEditor}
              onChangeTransform={(slotKey, transform) => apply(setSlotTransform(doc, activePage.id, slotKey, transform))}
              onChangeCaption={(slotKey, caption) => apply(setSlotCaption(doc, activePage.id, slotKey, caption))}
              annotationTool={annotationTool}
              selectedAnnotationId={selectedAnnotationId}
              pageAnnotations={activePage.annotations ?? []}
              onChangePageAnnotations={(anns) => apply(setPageAnnotations(doc, activePage.id, anns))}
              newTextSize={newTextSize}
              newArrowHeadSize={newArrowHeadSize}
              newColor={newColor}
              onSelectAnnotation={setSelectedAnnotationId}
              onDropPhoto={handleDropPhoto}
              onPickFromLibrary={(slotKey) => setPicker({ kind: 'slot', slotKey })}
              uploadingSlotKey={uploadingSlotKey}
            />
          ) : (
            <div className="ins-editor-empty">Add a page to begin.</div>
          )}
        </main>

        {/* Contextual inspector — basic for now; zoom/pan, annotation, and rich
            text tools arrive in their own issues. */}
        <aside className="ins-editor-inspector">
          {activePage && activePageId !== COVER && (
            <div className="ins-anno-tools">
              <h3>Annotate</h3>
              <div className="flex gap-1">
                {([
                  ['select', MousePointer2, 'Select / move'],
                  ['arrow', ArrowUpRight, 'Arrow'],
                  ['line', Minus, 'Line'],
                  ['dimension', MoveHorizontal, 'Measurement line'],
                  ['circle', CircleIcon, 'Circle'],
                  ['rect', SquareIcon, 'Rectangle'],
                  ['text', Type, 'Text'],
                ] as const).map(([tool, Icon, title]) => (
                  <button
                    key={tool}
                    aria-label={title}
                    data-tip={title}
                    className={`ins-tip px-2 py-1 text-sm rounded border ${annotationTool === tool ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700'}`}
                    onClick={() => setAnnotationTool(tool)}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                ))}
              </div>
              {/* Color applies to every annotation type, so the swatches are
                  always shown — with nothing selected they set what the next
                  annotation is drawn in. */}
              <div className="ins-anno-swatches" role="group" aria-label="Annotation color">
                {ANNO_COLORS.map((c) => {
                  const selected = (activePage.annotations ?? []).find((a) => a.id === selectedAnnotationId);
                  // With something selected the row shows THAT annotation's
                  // color; otherwise it shows what the next one will be drawn in.
                  const active = (selected ? selected.color ?? DEFAULT_ANNO_COLOR : newColor) === c.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      title={c.label}
                      aria-label={c.label}
                      aria-pressed={active}
                      className={`ins-anno-swatch${active ? ' is-active' : ''}`}
                      style={{ background: c.hex }}
                      onClick={() => {
                        setNewColor(c.key);
                        if (selected) {
                          apply(setPageAnnotations(doc, activePage.id, setAnnotationColor(activePage.annotations ?? [], selected.id, c.key)));
                        }
                      }}
                    />
                  );
                })}
              </div>
              {/* Clipboard actions — the same thing Cmd/Ctrl+C/X/V do, spelled
                  out for anyone on a trackpad who would not guess the shortcut. */}
              <div className="flex gap-1">
                {([
                  ['Copy', 'C', Copy, copyAnnotation, !selectedAnnotation],
                  ['Cut', 'X', Scissors, cutAnnotation, !selectedAnnotation],
                  ['Paste', 'V', ClipboardPaste, pasteAnnotation, !annoClipboard],
                ] as const).map(([label, key, Icon, onClick, disabled]) => (
                  <button
                    key={label}
                    aria-label={`${label} (${MOD_KEY}${key})`}
                    data-tip={`${label} (${MOD_KEY}${key})`}
                    disabled={disabled}
                    className="ins-tip inline-flex items-center gap-1 px-2 py-1 text-sm rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-40"
                    onClick={onClick}
                  >
                    <Icon className="h-4 w-4" /> {label}
                  </button>
                ))}
              </div>
              {/* Size controls for the selected annotation. Each also becomes the
                  size the next drawn annotation of that kind starts at. */}
              {(() => {
                const selected = selectedAnnotation;
                const setAnnotations = (next: Parameters<typeof setPageAnnotations>[2]) =>
                  apply(setPageAnnotations(doc, activePage.id, next));
                if (selected?.type === 'text') {
                  // Point sizes are derived from the stored viewBox units, so a
                  // size that isn't one of the menu's (a legacy note lifted off a
                  // photo, say) still shows its own value rather than snapping.
                  const pt = Math.round(annoUnitsToPt(selected.size ?? DEFAULT_ANNO_TEXT_SIZE));
                  const options = [...new Set([...ANNO_TEXT_PT_SIZES, pt])].sort((a, b) => a - b);
                  return (
                    <label className="ins-size-row">
                      Note size
                      <select
                        className="rounded border border-gray-300 px-2 py-1 text-sm"
                        value={pt}
                        onChange={(e) => {
                          const units = annoPtToUnits(Number(e.target.value));
                          setNewTextSize(units);
                          setAnnotations(setTextSize(activePage.annotations ?? [], selected.id, units));
                        }}
                      >
                        {options.map((s) => (
                          <option key={s} value={s}>{s} pt</option>
                        ))}
                      </select>
                    </label>
                  );
                }
                if (selected?.type === 'arrow' || selected?.type === 'dimension' || selected?.type === 'line') {
                  // One slider for every segment type; what it scales depends on
                  // the type — an arrow's head, a dimension's whole shape, a
                  // plain line's thickness.
                  const head = segmentSize(selected) ?? DEFAULT_ARROW_HEAD_SIZE;
                  const label = selected.type === 'arrow' ? 'Arrow head' : selected.type === 'line' ? 'Thickness' : 'Size';
                  return (
                    <label className="ins-size-row">
                      {label}
                      <input
                        type="range"
                        min={MIN_ARROW_HEAD_SIZE}
                        max={MAX_ARROW_HEAD_SIZE}
                        step={0.5}
                        value={head}
                        onChange={(e) => {
                          const size = Number(e.target.value);
                          setNewArrowHeadSize(size);
                          setAnnotations(setSegmentSize(activePage.annotations ?? [], selected.id, size));
                        }}
                      />
                      <span className="ins-size-val">{(head / DEFAULT_ARROW_HEAD_SIZE).toFixed(1)}×</span>
                    </label>
                  );
                }
                return null;
              })()}
              {selectedAnnotationId && (
                <button
                  aria-label="Delete annotation (Delete)"
                  data-tip="Delete annotation (Delete)"
                  className="ins-tip inline-flex items-center gap-1 px-2 py-1 text-sm rounded border border-gray-300 bg-white text-red-600 self-start"
                  onClick={deleteSelectedAnnotation}
                >
                  <Trash2 className="h-4 w-4" /> Delete annotation
                </button>
              )}
            </div>
          )}
          {region?.kind === 'slot' && activePage ? (
            <div>
              <h3>Photo slot</h3>
              {/* The library leads: the photo is usually already on the job, and
                  an upload from here now files one there anyway. */}
              <button
                type="button"
                className="ins-upload-btn"
                onClick={() => setPicker({ kind: 'slot', slotKey: region.key })}
              >
                Choose from job photos
              </button>
              <label className="ins-upload-btn ins-upload-btn-secondary">
                {activePage.slots[region.key]?.fileId ? 'Replace with an upload' : 'Upload a new photo'}
                <input
                  type="file"
                  accept="image/*,.heic,.heif"
                  hidden
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleUpload(region.key, f); e.target.value = ''; }}
                />
              </label>
              {activePage.slots[region.key]?.fileId && (
                <button
                  type="button"
                  className="ins-upload-btn ins-upload-btn-danger"
                  onClick={() => apply(clearSlotPhoto(doc, activePage.id, region.key))}
                >
                  <Trash2 className="h-4 w-4" /> Remove photo
                </button>
              )}
              {/* Clipboard row for the photo itself. Labelled "photo" because
                  the Annotate panel above carries its own Copy/Cut/Paste for
                  annotations, and the two rows are on screen together. Wraps:
                  the three labels are wider than the 300px inspector column.
                  Cut overlaps with Remove photo above by design — it is the same
                  emptying, with the photo kept on the clipboard. */}
              <div className="flex flex-wrap gap-1">
                {([
                  ['Copy photo', 'C', Copy, copySlotPhoto, !selectedSlotClip],
                  ['Cut photo', 'X', Scissors, cutSlotPhoto, !selectedSlotClip],
                  ['Paste photo', 'V', ClipboardPaste, pastePhotoIntoSlot, !photoClipboard],
                ] as const).map(([label, key, Icon, onClick, disabled]) => (
                  <button
                    key={label}
                    type="button"
                    aria-label={`${label} (${MOD_KEY}${key})`}
                    data-tip={`${label} (${MOD_KEY}${key})`}
                    disabled={disabled}
                    className="ins-tip inline-flex items-center gap-1 px-2 py-1 text-sm rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-40"
                    onClick={onClick}
                  >
                    <Icon className="h-4 w-4" /> {label}
                  </button>
                ))}
              </div>
              <Input
                placeholder="Caption"
                value={activePage.slots[region.key]?.caption ?? ''}
                onChange={(e) => apply(setSlotCaption(doc, activePage.id, region.key, e.target.value))}
              />
              {activePage.slots[region.key]?.fileId && (() => {
                const t = activePage.slots[region.key]!.transform;
                return (
                  <div className="ins-zoom">
                    <label className="ins-zoom-row">
                      Zoom
                      <input
                        type="range"
                        min={1}
                        max={4}
                        step={0.05}
                        value={t.zoom}
                        onChange={(e) => {
                          const z = Number(e.target.value);
                          apply(setSlotTransform(doc, activePage.id, region.key, {
                            zoom: z,
                            offsetX: clampPanOffset(t.offsetX, z),
                            offsetY: clampPanOffset(t.offsetY, z),
                          }));
                        }}
                      />
                      <span className="ins-zoom-val">{t.zoom.toFixed(1)}×</span>
                    </label>
                    <button
                      className="px-2 py-1 text-sm rounded border border-gray-300 bg-white text-gray-700 self-start"
                      onClick={() => apply(setSlotTransform(doc, activePage.id, region.key, { zoom: 1, offsetX: 0, offsetY: 0 }))}
                    >
                      Reset
                    </button>
                    {t.zoom > 1 && (() => {
                      const fileId = activePage.slots[region.key]?.fileId;
                      const url = fileId ? photoUrls[fileId] : undefined;
                      return url ? (
                        <PhotoNavigator
                          photoUrl={url}
                          transform={t}
                          aspectRatio={slotAspect ?? 4 / 3}
                          onChange={(offsetX, offsetY) =>
                            apply(setSlotTransform(doc, activePage.id, region.key, { zoom: t.zoom, offsetX, offsetY }))
                          }
                        />
                      ) : null;
                    })()}
                  </div>
                );
              })()}
            </div>
          ) : region?.kind === 'block' && activePage ? (
            <div>
              <h3>Text formatting</h3>
              {activeEditor ? (
                <RichTextToolbar
                  editor={activeEditor}
                  vAlign={activePage.blockMeta?.[region.key]?.vAlign ?? 'top'}
                  onVAlignChange={(v) => apply(setBlockVAlign(doc, activePage.id, region.key, v))}
                />
              ) : (
                <p className="ins-inspector-hint">Click into the text on the page to format it.</p>
              )}
            </div>
          ) : activePageId === COVER ? (
            <div className="ins-report-details">
              <h3>Report details</h3>
              <label className="ins-field-label">
                Title
                <Input
                  placeholder="Report title"
                  value={doc.header.title}
                  onChange={(e) => apply(setHeader(doc, { title: e.target.value }))}
                />
              </label>
              <div className="ins-field-label">
                Property
                <div className="ins-readonly-field">{jobAddress || "From the job's location"}</div>
              </div>
              <label className="ins-field-label">
                Inspection date
                <Input
                  type="date"
                  value={doc.header.date}
                  onChange={(e) => apply(setHeader(doc, { date: e.target.value }))}
                />
              </label>
              <div className="ins-field-label">
                Building photo
                <div className="ins-cover-photo-actions">
                  <button
                    type="button"
                    className="ins-upload-btn"
                    onClick={() => setPicker({ kind: 'cover' })}
                  >
                    Choose from job photos
                  </button>
                  <label className="ins-upload-btn ins-upload-btn-secondary">
                    {coverFileId ? 'Replace with an upload' : 'Upload'}
                    <input
                      type="file"
                      accept="image/*,.heic,.heif"
                      hidden
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleCoverUpload(f); e.target.value = ''; }}
                    />
                  </label>
                  {coverFileId && (
                    <button
                      className="inline-flex items-center gap-1 px-2 py-1 text-sm rounded border border-gray-300 bg-white text-red-600"
                      onClick={() => apply(setHeader(doc, { coverFileId: null }))}
                    >
                      <Trash2 className="h-4 w-4" /> Remove
                    </button>
                  )}
                </div>
              </div>
              <label className="ins-field-label">
                Issue summary
                <Textarea
                  placeholder="Brief summary of the issue(s)…"
                  value={(doc.header.issueSummary ?? []).map((b) => b.runs.map((r) => r.text).join('')).join('\n')}
                  onChange={(e) =>
                    apply(setHeader(doc, {
                      issueSummary: e.target.value ? [{ style: 'body', runs: [{ text: e.target.value }] }] : undefined,
                    }))
                  }
                />
              </label>
              <p className="ins-inspector-hint">The property address and letterhead are added to a generated cover sheet automatically.</p>
            </div>
          ) : (
            <p className="ins-inspector-hint">Select a photo slot, or click a text area to type on the page.</p>
          )}
        </aside>
      </div>

      {jobId && (
        <JobPhotoPickerDialog
          jobId={jobId}
          open={picker !== null}
          onOpenChange={(open) => { if (!open) setPicker(null); }}
          isUsed={(file) => usedFileIds.has(file.id)}
          usedLabel="In this report"
          description={
            pickerSlots > 1
              ? `Photos already on this job. Pick up to ${pickerSlots} — they fill the empty slots on this page in order.`
              : undefined
          }
          maxSelection={pickerSlots}
          onConfirm={(entries) => { void handlePickedPhotos(entries); }}
        />
      )}

      <Dialog open={pageToDelete !== null} onOpenChange={(open) => { if (!open) setPageToDelete(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this page?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            The page and its photos, annotations, and text will be removed. You can still undo this.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPageToDelete(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!pageToDelete) return;
                const next = removePage(doc, pageToDelete);
                apply(next);
                if (activePageId === pageToDelete) setActivePageId(next.pages[0]?.id ?? COVER);
                setPageToDelete(null);
              }}
            >
              Delete page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pendingDrop !== null} onOpenChange={(open) => { if (!open) setPendingDrop(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replace this photo?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            This slot already has a photo. Replacing it discards its zoom and pan.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDrop(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!pendingDrop) return;
                const { slotKey, file } = pendingDrop;
                setPendingDrop(null);
                void handleUpload(slotKey, file);
              }}
            >
              Replace photo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RecoverEditsDialog
        open={autosave.pendingRecovery !== null}
        onRecover={() => {
          const recovered = autosave.pendingRecovery;
          if (recovered) {
            setDoc(recovered);
            if (!recovered.pages.some((p) => p.id === activePageId)) {
              setActivePageId(recovered.pages[0]?.id ?? null);
            }
          }
          autosave.acceptRecovery();
        }}
        onDiscard={autosave.discardRecovery}
      />
    </div>
  );
}
